/**
 * DateFilterTabs - Simple date range filter tabs
 * Supports Today, Week, Month, Year filters
 */

import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';

export type DateRangeType = 'all' | 'today' | 'week' | 'month' | 'year' | 'custom';

export interface DateRange {
  type: DateRangeType;
  startDate: Date | null;
  endDate: Date | null;
  label: string;
}

interface MiniCalendarSidebarProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  className?: string;
}

// Quick filter options
const QUICK_FILTERS = [
  { type: 'today' as const, label: 'Hoy' },
  { type: 'week' as const, label: 'Semana' },
  { type: 'month' as const, label: 'Mes' },
  { type: 'year' as const, label: 'Año' },
  { type: 'all' as const, label: 'Todas' },
];

function getDateRangeForType(type: DateRangeType): DateRange {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (type) {
    case 'today':
      return {
        type: 'today',
        startDate: today,
        endDate: today,
        label: 'Hoy',
      };
    case 'week': {
      const startOfWeek = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday
      startOfWeek.setDate(today.getDate() + diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return {
        type: 'week',
        startDate: startOfWeek,
        endDate: endOfWeek,
        label: 'Esta semana',
      };
    }
    case 'month': {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        type: 'month',
        startDate: startOfMonth,
        endDate: endOfMonth,
        label: 'Este mes',
      };
    }
    case 'year': {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      return {
        type: 'year',
        startDate: startOfYear,
        endDate: endOfYear,
        label: `${now.getFullYear()}`,
      };
    }
    case 'all':
    default:
      return {
        type: 'all',
        startDate: null,
        endDate: null,
        label: 'Todas las fechas',
      };
  }
}

export function MiniCalendarSidebar({
  dateRange,
  onDateRangeChange,
  className,
}: MiniCalendarSidebarProps) {
  const handleQuickFilter = (type: DateRangeType) => {
    const newRange = getDateRangeForType(type);
    onDateRangeChange(newRange);
    // Save to localStorage
    try {
      localStorage.setItem('bookings-date-filter', type);
    } catch {
      // Ignore localStorage errors
    }
  };

  return (
    <div className={cn('bg-white rounded-lg border border-gray-200 p-3', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-gray-400" />
        <span className="text-xs font-medium text-gray-700">Fecha de cita</span>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-col gap-1">
        {QUICK_FILTERS.map((filter) => (
          <button
            key={filter.type}
            onClick={() => handleQuickFilter(filter.type)}
            className={cn(
              'px-3 py-2 text-xs font-medium rounded transition-colors text-left',
              dateRange.type === filter.type
                ? 'bg-blue-600 text-white'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Current Filter Display */}
      {dateRange.type !== 'all' && dateRange.type !== 'today' && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <div className="py-1.5 px-2 bg-blue-50 rounded text-center">
            <span className="text-[10px] font-medium text-blue-700">
              {dateRange.label}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to get initial date range - reads from localStorage, defaults to 'all'
export function getInitialDateRange(): DateRange {
  try {
    const saved = localStorage.getItem('bookings-date-filter') as DateRangeType | null;
    if (saved && ['all', 'today', 'week', 'month', 'year'].includes(saved)) {
      return getDateRangeForType(saved);
    }
  } catch {
    // Ignore localStorage errors
  }
  return getDateRangeForType('all');
}

// Export for reuse
export { getDateRangeForType };
